import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base_URL } from "../../../config/config";
import { MdOutlineArrowLeft, MdOutlineArrowRight } from "react-icons/md";
import axios from "axios";
import moment from "moment";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { FiTrash } from 'react-icons/fi';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { deleteVendor } from "../../../api/vendor";

export const fetchAllUsers = async ({ page = 1, limit = 10, showRegistered = false, showSubscribed = false, search = null }) => {
  const accessToken = localStorage.getItem('adminAccessToken');
  let defaultCriteria = { criteria: [] };
  if (search) defaultCriteria.globalSearch = { type: "user", value: search };

  const { data } = await axios.get(
    `${base_URL}/all-users?page=${page}&limit=${limit}&showRegistered=${showRegistered}&showSubscribed=${showSubscribed}&filter=${JSON.stringify(defaultCriteria)}`,
    {
      headers: { Authorization: `Bearer ${accessToken}` }
    }
  );

  return data;
};

const useAllUsers = (page, limit, showRegistered, showSubscribed, search) => {
  return useQuery({
    queryKey: ["allUsersadmin", page, limit, showRegistered, showSubscribed, search],
    queryFn: () => fetchAllUsers({ page, limit, showRegistered, showSubscribed, search }),
    keepPreviousData: true,
  });
};

function GetAllUsers() {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showRegistered, setShowRegistered] = useState(false);
  const [showSubscribed, setShowSubscribed] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRows, setExpandedRows] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState(null);

  const queryClient = useQueryClient();
  const role = localStorage.getItem("role");

  const {
    data: usersData,
    isLoading,
  } = useAllUsers(currentPage, itemsPerPage, showRegistered, showSubscribed, searchTerm);

  const deleteMutation = useMutation({
    mutationFn: deleteVendor,
    onSuccess: (data) => {
      toast.success(data?.message || 'Vendor deleted successfully');
      queryClient.invalidateQueries({ queryKey: ["allUsersadmin"] });
      setShowConfirmDialog(false);
    },
    onError: (error) => {
      toast.error(`Delete failed: ${error?.response?.data?.message || error.message}`);
    },
  });

  const confirmDelete = (vendorId) => {
    setSelectedVendorId(vendorId);
    setShowConfirmDialog(true);
  };

  const handleDelete = () => {
    if (selectedVendorId) {
      deleteMutation.mutate(selectedVendorId);
    }
  };

  const rowExpansionTemplate = (data) => (
    <DataTable value={data.teamMembers}>
      <Column field="" header="Sno" style={{ width: '4rem' }} body={(rowData, options) => (
        <div className="w-full text-center">{options.rowIndex + 1}</div>
      )} />
      <Column field="name" header="Name" />
      <Column field="email" header="Email" />
      <Column field="phone" header="Phone" />
      <Column field="isVerified" header="Activation Status" body={(rowData) => (
        <Tag
          value={rowData.isVerified ? 'Verified' : 'Pending'}
          severity={rowData.isVerified ? 'success' : 'warning'}
          className="text-sm px-3 py-1 rounded-md"
        />
      )} />
    </DataTable>
  );

  return (
    <div className="overflow-x-auto px-5 flex flex-col gap-5 pb-16 pt-4 relative">
      {/* Custom Confirmation Modal */}
      {showConfirmDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 relative">
            <div className="flex flex-col items-center text-center space-y-4">
              <ExclamationTriangleIcon className="w-12 h-12 text-red-500" />
              <h2 className="text-xl font-semibold text-gray-800">Delete Confirmation</h2>
              <p className="text-gray-600">Are you sure you want to delete this vendor? This action cannot be undone.</p>
              <div className="flex justify-center gap-4 mt-4">
                <button
                  onClick={() => setShowConfirmDialog(false)}
                  className="px-4 py-2 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex items-center gap-6 mb-4 w-full">
        <input
          type="text"
          placeholder="Search by Name, Email, Phone or Type"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border rounded px-4 py-2 w-full max-w-md"
        />
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showRegistered}
              onChange={(e) => {
                setShowRegistered(e.target.checked);
                setShowSubscribed(false);
              }}
              className="form-checkbox h-4 w-4"
            />
            <span>Show Only Registered</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showSubscribed}
              onChange={(e) => {
                setShowSubscribed(e.target.checked);
                setShowRegistered(false);
              }}
              className="form-checkbox h-4 w-4"
            />
            <span>Show Only Subscribed</span>
          </label>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        value={usersData?.data ?? []}
        tableStyle={{ minWidth: '50rem' }}
        rowExpansionTemplate={rowExpansionTemplate}
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowClassName={(rowData) => rowData?.isVendorMatched && role !== "SUPER_ADMIN" ? 'bg-red-200' : 'bg-white'}
      >
        <Column header="Sl No." body={(data, options) => (
          <div className="text-center">{((currentPage - 1) * itemsPerPage + options.rowIndex + 1)}</div>
        )} style={{ width: '4rem' }} />

       <Column header="Name" body={(rowData) => (
          <Link to={rowData?.isVendorMatched ? "/vendor-dashboard-editor" : "/user-details"} state={{ user: rowData }} className="text-blue-500 underline">
            {rowData.fullName}
          </Link>
        )} />

        <Column header="Email" body={(rowData) => (
          <Link to={rowData?.isVendorMatched ? "/vendor-dashboard-editor" : "/user-details"} state={{ user: rowData }} className="text-blue-500 underline">
            {rowData.email}
          </Link>
        )} />

        <Column field="mobileNumber" header="Phone" />

        {showSubscribed && (
          <Column
            header="No. of Team Members"
            body={(rowData) => {
              const isExpanded = expandedRows?.some(row => row._id === rowData._id);
              const canExpand = rowData.teamMembers?.length > 0;
              return (
                <div className="flex items-center gap-4">
                  <span>{rowData.noOfTeamMembers || "N/A"}</span>
                  {canExpand && (
                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        const expanded = [...expandedRows];
                        const index = expanded.findIndex(row => row._id === rowData._id);
                        if (index >= 0) expanded.splice(index, 1);
                        else expanded.push(rowData);
                        setExpandedRows(expanded);
                      }}
                    >
                      <i className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'} text-xs`} />
                    </div>
                  )}
                </div>
              );
            }}
          />
        )}

        <Column
          header="Joined"
          body={(rowData) => moment(showSubscribed ? rowData?.onBoardingDetails?.addedOn : rowData?.createdAt).format("DD-MM-YYYY")}
        />

        <Column
          header="Expiry"
          body={(rowData) => {
            if(showSubscribed) {
              let date = moment.parseZone(rowData?.trialEnd).format("DD-MM-YYYY")
              return date ?? "";
            }
            else return "";
          }}
        />

        {/* {role === "SUPER_ADMIN" && (
          <Column
            header="Actions"
            body={(rowData) => (
              <div
                onClick={() => confirmDelete(rowData._id)}
                title="Delete"
                className="cursor-pointer flex items-center justify-center"
              >
                <div className="p-1.5 rounded-full hover:bg-red-100 text-red-600 transition-all duration-150 text-center">
                  <FiTrash size={18} />
                </div>
              </div>
            )}
            style={{ textAlign: 'center', width: '60px' }}
          />
        )} */}
      </DataTable>

      {/* Pagination */}
      <div className="flex justify-center items-center relative bottom-0 gap-5">
        <span className="text-black">Page</span>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((prev) => prev - 1)}
            disabled={currentPage === 1}
            className="disabled:opacity-50"
          >
            <MdOutlineArrowLeft className="text-primary text-3xl" />
          </button>
          <div className="min-w-[24px] h-[24px] flex bg-primary text-white justify-center items-center rounded-full">
            {currentPage}
          </div>
          <button
            onClick={() => setCurrentPage((prev) => prev + 1)}
            disabled={currentPage === usersData?.totalPages}
            className="disabled:opacity-50"
          >
            <MdOutlineArrowRight className="text-primary text-3xl" />
          </button>
        </div>
        <select
          value={itemsPerPage}
          onChange={(e) => {
            setCurrentPage(1);
            setItemsPerPage(parseInt(e.target.value, 10));
          }}
          className="py-2 bg-white border-b border-blue-200 rounded"
        >
          {[10, 20, 30, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}
        </select>
      </div>
    </div>
  );
}

export default GetAllUsers;
